import React, {useState, useEffect} from 'react';
import {Table, Typography, Card, message, Alert, Select, Spin, Empty, Space} from 'antd';
import GoToUserInfoButton from '../../shared/components/buttons/GoToUserInfoButton';
import {internalApi} from '../../common/axios';
import dayjs from 'dayjs';

const {Title, Text} = Typography;
const {Option} = Select;

const LevelAbuseDetectionTab = () => {
    const [snapshotDates, setSnapshotDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentSnapshot, setCurrentSnapshot] = useState(null);
    const [loadingDates, setLoadingDates] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);

    const columns = [
        {title: '유저 PID', dataIndex: 'pid', key: 'pid'},
        {title: '닉네임', dataIndex: 'nickname', key: 'nickname'},
        {title: '계정 생성일', dataIndex: 'created', key: 'created', render: (text) => text ? dayjs.unix(text).format('YYYY-MM-DD HH:mm') : '-',},
        {title: '현재 레벨', dataIndex: 'level', key: 'level', sorter: (a, b) => a.level - b.level, defaultSortOrder: 'descend',},
        {title: '국가', dataIndex:'country', key: 'country'},
        {title: '유저 정보', dataIndex: 'pid', key: 'action', render: (pid) => <GoToUserInfoButton pid={pid}/>,},
    ];

    useEffect(() => {
        const fetchSnapshotDates = async () => {
            setLoadingDates(true);
            setError(null);
            try {
                const {data} = await internalApi.get('/logs/hack/level-abuse-snapshots/dates');
                setSnapshotDates(data.dates);
            } catch (err) {
                console.error("스냅샷 날짜 목록 조회 실패:", err);
                const errorMessage = "스냅샷 날짜 목록을 불러오는 중 오류가 발생했습니다.";
                setError(errorMessage);
                message.error(errorMessage);
            } finally {
                setLoadingDates(false);
            }
        };

        void fetchSnapshotDates();
    }, []);

    const handleDateChange = async (date) => {
        setSelectedDate(date);
        if (!date) {
            setCurrentSnapshot(null);
            return;
        }

        setLoadingDetails(true);
        setError(null);
        try {
            const {data} = await internalApi.get(`/logs/hack/level-abuse-snapshot?date=${date}`);
            setCurrentSnapshot(data.rows);
        } catch (err) {
            console.error(`${date} 스냅샷 상세 조회 실패:`, err);
            const errorMessage = "데이터를 불러오는 중 오류가 발생했습니다.";
            setError(errorMessage);
            message.error(errorMessage);
            setCurrentSnapshot(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const renderDetails = () => {
        if (loadingDetails) {
            return (<div style={{textAlign: 'center', padding: '50px'}}>
                <Space direction="vertical" size="large">
                    <Spin size="large"/>
                    <Text>데이터 로딩 중...</Text>
                </Space>
            </div>);
        }
        if (error) {
            return <Alert message="오류 발생" description={error} type="error" showIcon/>;
        }
        if (!selectedDate) {
            return <Alert message="날짜를 선택하면 상세 데이터를 조회합니다." type="info" showIcon/>;
        }
        if (!currentSnapshot) {
            return <Empty description="선택한 날짜의 데이터가 없습니다."/>;
        }

        return (<>
            <Alert
                message={`스냅샷 기준: ${currentSnapshot.criteria.days || 'N/A'}일 내 ${currentSnapshot.criteria.level || 'N/A'}레벨 달성 유저`}
                description={`데이터 기준일: ${dayjs(currentSnapshot.lastUpdated).format('YYYY-MM-DD HH:mm:ss')}`}
                type="info"
                showIcon
                style={{marginBottom: '24px'}}
            />
            <Table
                rowKey="pid"
                columns={columns}
                dataSource={currentSnapshot.users}
                variant="outlined"
                title={() => `총 ${currentSnapshot.users.length} 명의 유저`}
            />
        </>);
    };

    return (<div style={{padding: '24px'}}>
        <Card>
            <Title level={4} style={{marginBottom: '16px'}}>레벨 어뷰징 감지</Title>
            <Text type="secondary" style={{display: 'block', marginBottom: '24px'}}>
                조회하고 싶은 날짜를 선택하면 해당일 새벽에 생성된 스냅샷 데이터를 볼 수 있습니다.
            </Text>

            <Spin spinning={loadingDates} tip="날짜 목록 로딩 중...">
                <Select
                    style={{width: 240, marginBottom: '24px'}}
                    placeholder="조회 날짜 선택"
                    onChange={handleDateChange}
                    allowClear
                    disabled={loadingDates} // 로딩 중에는 비활성화
                >
                    {snapshotDates.map(date => (<Option key={date} value={date}>{date}</Option>))}
                </Select>
            </Spin>

            {/* 상세 데이터 영역 */}
            <div style={{marginTop: '24px'}}>
                {renderDetails()}
            </div>
        </Card>
    </div>);
};

export default LevelAbuseDetectionTab;